import React, { useState, createRef, useRef } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { useOnClickOutside } from 'usehooks-ts';
import { useHistory } from 'react-router-dom';
import { Tooltip } from '@material-ui/core';
import cx from 'classnames';

import { editFolderName, postFolderViewRequest } from '../../../api/foldersApi';
import { selectShareFolder, setClickedFolderName, switchShareModal } from '../../../store/folders/foldersSlice';
import { ACCESS_TYPE, ACCESS_TYPE_ENUM } from '../../../enums/ACCESS_ENUM';
import { bytesToGB, bytesToMB, secondsToHours, useDimensions } from '../../../helpers';
import FolderIcon from '../../../assets/icons/folder-open.svg';
import { DateFormatter } from '../../../helpers/dateFormatter';
import { FOLDER_SHARED_WITH_ME, FOLDER_TYPE } from '../../../enums/TYPES';
import StorageIcon from '../../../assets/icons/storage.svg';
import { MoreIcon } from '../../../assets/icons/threedots';
import { useAppDispatch } from '../../../store/hooks';
import DeleteFolderModal from '../DeleteFolderModal';
import styles from './AllContentFolder.module.scss';
import TrashButton from '../../modules/TrashButton';
import EditButton from '../../modules/EditButton';
import ShareButton from '../../modules/ShareButton';
import { NOTIFICATION } from '../../../enums';
import { PATHS } from '../../../enums/PATHS';
import { convertSeconds } from '../../Timeline/utils';
import { SortOptions } from '../../../enums/SORT';
import { getDateColumnTooltipLabel, getDateColumnTooltipValue, getDateColumnValue } from '../../modules/DateSortHeader';

type TComposerFolder = {
  folder: FOLDER_SHARED_WITH_ME;
  fetchDataCallback?: () => void;
  dateSortOption?: SortOptions | null;
};

const AllContentFolder: React.FC<TComposerFolder> = ({ folder, fetchDataCallback, dateSortOption }) => {
  const history = useHistory();
  const notification = useSnackbar();
  const redirectRef = createRef<HTMLDivElement>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const dispatch = useAppDispatch();
  const [folderName, setFolderName] = useState(folder.name);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  const [showDotsMenu, setShowDotsMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setFolderName(e.target.value);
  };

  const handleEdit = async () => {
    if (folderName.trim().length === 0) {
      setFolderName(folder.name);
      setTimeout(() => {
        notification.showMessage(NOTIFICATION.EMPTY);
      }, 100);

      return;
    }

    setIsEditing(!isEditing);
    if (folderName.trim() === folder.name.trim()) return;
    await editFolderName(folderName, folder.id);
    if (fetchDataCallback) {
      fetchDataCallback();
    }
  };

  const handleShare = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(selectShareFolder(folder as unknown as FOLDER_TYPE));
    dispatch(switchShareModal(true));
    setShowDotsMenu(false);
  };

  const switchDotsMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (showDotsMenu) {
      setShowDotsMenu(false);
      return;
    }

    const folderElement = e.currentTarget.closest(`.${styles.container}`) as HTMLElement;
    if (!folderElement) return;

    const folderRect = folderElement.getBoundingClientRect();
    const menuWidth = actionMenuRef.current?.offsetWidth || 310;
    const menuHeight = actionMenuRef.current?.offsetHeight || 300;

    let posX = e.clientX - folderRect.left;
    let posY = e.clientY - folderRect.top;

    // Ensure menu stays within folder boundaries
    if (posX + menuWidth > folderRect.width) {
      posX = folderRect.width - menuWidth - 10;
    }

    const p = folderRect.bottom + menuHeight - window.innerHeight;
    // Adjust if the menu would go below the viewport
    if (folderRect.bottom + menuHeight > window.innerHeight) {
      if (p > 90) {
        posY -= menuHeight - 150; // Move it upwards
      }
    }

    setMenuPosition({
      x: posX + 20,
      y: posY,
    });

    setShowDotsMenu(true);
  };

  const enableFolderNameEdit = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);
    setIsEditing(true);
    setTimeout(() => {
      if (inputRef && inputRef?.current) {
        inputRef.current.focus();
      }
    }, 150);
  };

  const redirectToContent = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isEditing) return;
    if (e.currentTarget.contains(e.target as Node)) {
      dispatch(setClickedFolderName(folder.name));
      postFolderViewRequest(e.currentTarget.id);
      history.push(`${PATHS.ALL_CONTENT_VIDEOFEEDS.path}?folderId=${e.currentTarget.id}&parentPage=${PATHS.SHARED_WITH_ME.label}`);
    }
  };

  const switchDeleteModal = (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    setShowDotsMenu(false);
    setShowDeleteModal(!showDeleteModal);
  };

  const handleFolderIconClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (isEditing) return;
    e.stopPropagation();
    dispatch(setClickedFolderName(folder.name));
    postFolderViewRequest(folder.id);
    history.push(`${PATHS.ALL_CONTENT_VIDEOFEEDS.path}?folderId=${folder.id}&parentPage=${PATHS.MY_FOLDERS.label}`);
  };

  const { widthD } = useDimensions();

  useOnClickOutside(actionMenuRef, (e) => {
    e.stopPropagation();
    setShowDotsMenu(false);
  });

  return (
    <div
      onClick={redirectToContent}
      onContextMenu={switchDotsMenu}
      id={folder.id}
      className={cx(styles.container, showDotsMenu && styles.selectedFolder)}
      style={{ position: 'relative' }}
    >
      <div ref={redirectRef} className={styles.link}>
        <div className={styles.leftSide} id={folder.id} onClick={redirectToContent}>
          <img src={FolderIcon} alt="folder" title="foldericon" style={{ cursor: 'pointer' }} onClick={handleFolderIconClick} />
          <Tooltip title={folder.name}>
            {isEditing ? (
              <form
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onSubmit={handleEdit}
              >
                <input className={styles.input} onChange={handleChange} value={folderName} onBlur={handleEdit} ref={inputRef} />
              </form>
            ) : (
              <div className={styles.name}>
                <span>{folderName}</span>
              </div>
            )}
          </Tooltip>
        </div>
      </div>
      <div className={styles.rightSide}>
        <Tooltip placement="bottom-start" title={folder?.owner?.username}>
          <div className={styles.team}>{folder.owner?.username}</div>
        </Tooltip>
        <Tooltip
          placement="bottom-start"
          title={folder?.highest_user_access ? ACCESS_TYPE[folder?.highest_user_access?.access?.title as ACCESS_TYPE_ENUM] : 'VIEW'}
        >
          <div className={styles.team}>
            {folder?.highest_user_access ? ACCESS_TYPE[folder?.highest_user_access?.access?.title as ACCESS_TYPE_ENUM] : 'VIEW'}
          </div>
        </Tooltip>

        <div className={styles.team}>My Folders</div>
        <Tooltip title={`${getDateColumnTooltipLabel(dateSortOption)}: ${DateFormatter(getDateColumnTooltipValue(folder, dateSortOption), true)}`}>
          <div className={styles.date}>
            {widthD > 1600
              ? DateFormatter(getDateColumnValue(folder, dateSortOption), true)
              : DateFormatter(getDateColumnValue(folder, dateSortOption), false)}
          </div>
        </Tooltip>
        <div className={styles.size}>
          <img src={StorageIcon} alt="storage" />
          {bytesToGB(Number(folder?.size)) > 1
            ? `${bytesToGB(Number(folder?.size)).toFixed(2)} GB`
            : `${bytesToMB(Number(folder?.size)).toFixed(2)} MB`}
        </div>

        <div className={styles.duration}>
          {folder?.duration && (
            <Tooltip title={convertSeconds(folder.duration, false, true)}>
              <div>{secondsToHours(folder.duration)}</div>
            </Tooltip>
          )}
        </div>

        <div className={styles.dotsMenuBtn} onClick={switchDotsMenu}>
          <div className={styles.dotIcon}>
            <MoreIcon />
          </div>
        </div>
      </div>

      <div
        ref={actionMenuRef}
        className={styles.actionMenu}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          display: showDotsMenu ? 'block' : 'none',
          top: `${menuPosition.y}px`,
          left: `${menuPosition.x}px`,
          cursor: 'auto',
        }}
      >
        <div className={styles.menuOption} onClick={enableFolderNameEdit}>
          <EditButton /> Rename
        </div>
        <div className={styles.menuOption} onClick={handleShare}>
          <ShareButton /> Share
        </div>
        <div className={styles.menuOption} onClick={switchDeleteModal}>
          <TrashButton /> Delete
        </div>
      </div>

      <DeleteFolderModal
        owner={folder.owner}
        open={showDeleteModal}
        switchModal={() => setShowDeleteModal(!showDeleteModal)}
        folderId={folder.id}
        fetchDataCallback={fetchDataCallback}
      />
    </div>
  );
};

export default AllContentFolder;
